/**
 * SavedCalculation API Service
 *
 * Type-safe API functions for saved calculation operations.
 *
 * Business Rules:
 * - ONE calculation per user per year
 * - Users UPDATE existing calculations, don't create duplicates
 * - Always check for existing calculation before creating
 */

import { getCurrentUser } from 'aws-amplify/auth';
import { getAmplifyClient } from '../amplifyClient';

// Lazy client getter - ensures Amplify is configured before accessing models
const client = {
  get models() {
    return getAmplifyClient().models;
  }
};

/**
 * SavedCalculation Input Types
 */
export interface CreateSavedCalculationInput {
  userId: string;
  osd: 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA'; // OSD (Operator Systemu Dystrybucyjnego)
  calculationYear: number;
  monthlyConsumption: any; // JSON array[12]
  householdSize: number;
  hasHeatPump: boolean;
  hasSolarPanels: boolean;
  hasElectricVehicle: boolean;
  hourlyPattern: any; // JSON { weekday: [24], weekend: [24] }
  calculationResults: any; // JSON - all tariff comparison data
  version: number; // Start at 1
}

export interface UpdateSavedCalculationInput {
  id: string;
  osd?: 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA'; // OSD (can be updated)
  calculationYear?: number;
  monthlyConsumption?: any;
  householdSize?: number;
  hasHeatPump?: boolean;
  hasSolarPanels?: boolean;
  hasElectricVehicle?: boolean;
  hourlyPattern?: any;
  calculationResults?: any;
  version: number; // Required for optimistic locking
}

/**
 * SavedCalculation Response Type
 */
export interface SavedCalculation {
  id: string;
  userId: string;
  osd: 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA'; // OSD (Operator Systemu Dystrybucyjnego)
  calculationYear: number;
  monthlyConsumption: any;
  householdSize: number;
  hasHeatPump: boolean;
  hasSolarPanels: boolean;
  hasElectricVehicle: boolean;
  hourlyPattern: any;
  calculationResults: any;
  version: number;
  createdAt: string;
  updatedAt: string;
  owner: string;
}

/**
 * Get current authenticated user ID
 */
async function getCurrentUserId(): Promise<string> {
  try {
    const user = await getCurrentUser();
    return user.userId;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] getCurrentUserId error:', error);
    }
    throw new Error('User not authenticated');
  }
}

/**
 * Check if user has a saved calculation for a specific year
 *
 * CRITICAL: Always call this before creating a new saved calculation
 * to enforce one-per-year constraint
 *
 * @param userId - User ID
 * @param year - Calculation year (e.g., 2025)
 * @returns Existing calculation or null
 * @deprecated Use getSavedCalculationByUserOSDAndYear instead (Multi-OSD support)
 */
export async function getSavedCalculationByYear(
  userId: string,
  year: number
): Promise<SavedCalculation | null> {
  try {
    // Query using the savedCalculationsByUserAndYear GSI
    const response = await client.models.SavedCalculation.list({
      filter: {
        userId: { eq: userId },
        calculationYear: { eq: year },
      },
      limit: 1,
    });

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      throw new Error(`GraphQL query failed: ${JSON.stringify(response.errors)}`);
    }

    if (response.data && response.data.length > 0) {
      return response.data[0] as unknown as SavedCalculation;
    }

    return null;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] getSavedCalculationByYear error:', error);
      // Log additional error details if available
      if (error && typeof error === 'object' && 'errors' in error) {
        console.error('[SavedCalculation] GraphQL errors:', (error as { errors: unknown }).errors);
      }
    }
    throw error;
  }
}

/**
 * Get saved calculation by user, OSD, and year (Multi-OSD support)
 *
 * CRITICAL: Use this for Multi-OSD enabled calculators
 * Enforces one calculation per (user, OSD, year) constraint
 *
 * @param userId - User ID
 * @param osd - OSD (TAURON, PGE, ENEA, ENERGA)
 * @param year - Calculation year (e.g., 2025)
 * @returns Existing calculation or null
 */
export async function getSavedCalculationByUserOSDAndYear(
  userId: string,
  osd: 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA',
  year: number
): Promise<SavedCalculation | null> {
  try {
    // Query using the savedCalculationsByUserOSDAndYear GSI
    // GSI sort keys: ['osd', 'calculationYear']
    const response = await client.models.SavedCalculation.list({
      filter: {
        userId: { eq: userId },
        osd: { eq: osd },
        calculationYear: { eq: year },
      },
      limit: 1,
    });

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      throw new Error(`GraphQL query failed: ${JSON.stringify(response.errors)}`);
    }

    if (response.data && response.data.length > 0) {
      return response.data[0] as unknown as SavedCalculation;
    }

    return null;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] getSavedCalculationByUserOSDAndYear error:', error);
      // Log additional error details if available
      if (error && typeof error === 'object' && 'errors' in error) {
        console.error('[SavedCalculation] GraphQL errors:', (error as { errors: unknown }).errors);
      }
    }
    throw error;
  }
}

/**
 * Create or update a saved calculation for the current year
 *
 * Business Logic:
 * 1. Check if calculation exists for (user, OSD, current year)
 * 2. If exists: UPDATE with incremented version
 * 3. If not exists: CREATE new calculation
 *
 * @param input - Calculation input data (must include OSD)
 * @returns Created or updated calculation
 */
export async function saveCalculationForCurrentYear(
  input: Omit<CreateSavedCalculationInput, 'userId' | 'calculationYear' | 'version'>
): Promise<{ calculation: SavedCalculation; isUpdate: boolean }> {
  try {
    const userId = await getCurrentUserId();
    const currentYear = new Date().getFullYear();

    // Validate OSD is provided
    if (!input.osd) {
      throw new Error('OSD is required for saving calculation');
    }

    // Check if calculation already exists for (user, OSD, current year)
    const existingCalculation = await getSavedCalculationByUserOSDAndYear(
      userId,
      input.osd,
      currentYear
    );

    if (existingCalculation && existingCalculation.id) {
      // UPDATE existing calculation
      // CRITICAL: GraphQL JSON fields require stringified JSON
      const updateInput: UpdateSavedCalculationInput = {
        id: existingCalculation.id,
        osd: input.osd, // Update OSD if changed
        monthlyConsumption: JSON.stringify(input.monthlyConsumption) as any,
        householdSize: input.householdSize,
        hasHeatPump: input.hasHeatPump,
        hasSolarPanels: input.hasSolarPanels,
        hasElectricVehicle: input.hasElectricVehicle,
        hourlyPattern: JSON.stringify(input.hourlyPattern) as any,
        calculationResults: JSON.stringify(input.calculationResults) as any,
        version: existingCalculation.version + 1, // Optimistic locking
      };

      const response = await client.models.SavedCalculation.update(updateInput as any);

      if (!response.data) {
        // Development only logging
        if (import.meta.env.DEV) {
          console.error('[SavedCalculation] saveCalculationForCurrentYear update: no response data');
        }
        throw new Error('Nie otrzymano odpowiedzi z serwera podczas aktualizacji');
      }

      return {
        calculation: response.data as unknown as SavedCalculation,
        isUpdate: true,
      };
    }
      // CREATE new calculation
      // CRITICAL: GraphQL JSON fields require stringified JSON
      const createInput: CreateSavedCalculationInput = {
        userId,
        osd: input.osd, // REQUIRED: OSD field for Multi-OSD support
        calculationYear: currentYear,
        monthlyConsumption: JSON.stringify(input.monthlyConsumption) as any,
        householdSize: input.householdSize,
        hasHeatPump: input.hasHeatPump,
        hasSolarPanels: input.hasSolarPanels,
        hasElectricVehicle: input.hasElectricVehicle,
        hourlyPattern: JSON.stringify(input.hourlyPattern) as any,
        calculationResults: JSON.stringify(input.calculationResults) as any,
        version: 1, // Initial version
      };

      const response = await client.models.SavedCalculation.create(createInput as any);

      if (!response.data) {
        // Development only logging
        if (import.meta.env.DEV) {
          console.error('[SavedCalculation] saveCalculationForCurrentYear create: no response data');
        }
        throw new Error('Nie otrzymano odpowiedzi z serwera podczas tworzenia');
      }

      return {
        calculation: response.data as unknown as SavedCalculation,
        isUpdate: false,
      };

  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] saveCalculationForCurrentYear error:', error);
    }
    throw error;
  }
}

/**
 * Get a single saved calculation by ID
 */
export async function getSavedCalculation(id: string): Promise<SavedCalculation | null> {
  try {
    const response = await client.models.SavedCalculation.get({ id });
    return response.data as unknown as SavedCalculation;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] getSavedCalculation error:', error);
    }
    throw error;
  }
}

/**
 * List all saved calculations for the authenticated user
 */
export async function listSavedCalculations(options?: {
  limit?: number;
  nextToken?: string;
}): Promise<{ items: SavedCalculation[]; nextToken?: string }> {
  try {
    const response = await client.models.SavedCalculation.list({
      limit: options?.limit || 20,
      nextToken: options?.nextToken,
    });

    return {
      items: (response.data || []) as unknown as SavedCalculation[],
      nextToken: response.nextToken || undefined,
    };
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] listSavedCalculations error:', error);
    }
    throw error;
  }
}

/**
 * Update a saved calculation
 *
 * @param input - Update input with partial fields
 * @returns Updated calculation
 */
export async function updateSavedCalculation(
  input: UpdateSavedCalculationInput
): Promise<SavedCalculation> {
  try {
    const response = await client.models.SavedCalculation.update(input as any);

    if (!response.data) {
      // Development only logging
      if (import.meta.env.DEV) {
        console.error('[SavedCalculation] updateSavedCalculation: no response data');
      }
      throw new Error('Nie udało się zaktualizować kalkulacji');
    }

    return response.data as unknown as SavedCalculation;
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] updateSavedCalculation error:', error);
    }
    throw error;
  }
}

/**
 * Delete a saved calculation
 *
 * GDPR compliant: Users can delete their data at any time
 */
export async function deleteSavedCalculation(id: string): Promise<void> {
  try {
    await client.models.SavedCalculation.delete({ id });
  } catch (error) {
    // Development only logging
    if (import.meta.env.DEV) {
      console.error('[SavedCalculation] deleteSavedCalculation error:', error);
    }
    throw error;
  }
}
